import { useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/capacitor';

import { ILocationDecoded } from '@mopla/data-models';
import { useLogger } from '@mopla/logger';
import { LatLng } from '@mopla/utils';

import { reverseGeocode } from './location/useConvertLatlong';
import { lookupById } from './location/useDecodeLocationById';

type MapController = import('../services/hereapi/MapController').MapController;
type TIcons = import('../services/hereapi/MapController').TIcons;

interface IInitialGeoFieldValue {
	id: string;
	position: LatLng;
}

export interface IMapAddressPickerParameters {
	mapController: MapController | null;
	userPosition?: LatLng | null;
	initialGeoFieldValue?: IInitialGeoFieldValue | null;
	/** Which location field is being picked. Determines the cursor icon shown on the map. */
	pickerField?: 'departure' | 'arrival' | null;
}

const pickerFieldToIconName = (
	field: 'departure' | 'arrival' | null | undefined
): TIcons => {
	if (field === 'departure') return 'startPos';
	if (field === 'arrival') return 'destinationPos';
	return 'cursor';
};

export const useMapAddressPicker = (args: IMapAddressPickerParameters) => {
	const { mapController, userPosition, initialGeoFieldValue, pickerField } =
		args;
	const [pos, setPos] = useState<LatLng | null>(null);
	const [locationDecoded, setLocationDecoded] =
		useState<ILocationDecoded | null>(null);
	const [isReverseGeocoding, setReverseGeocoding] = useState(false);
	const lastUserPositionRef = useRef(userPosition);
	const logger = useLogger('useMapAddressPicker');

	/** Enable center pointer when mapController is ready, disable on unmount */
	useEffect(() => {
		if (!mapController) return;

		mapController.enableCenterPointer(pickerFieldToIconName(pickerField));
		mapController.onDragDebounced = setPos;
		mapController.onDrag = () => setReverseGeocoding(true);

		if (initialGeoFieldValue) {
			mapController.drawMarker(
				initialGeoFieldValue.position,
				pickerFieldToIconName(pickerField)
			);
		}

		return () => {
			mapController.disableCenterPointer();
			mapController.onDrag = undefined;
			mapController.onDragDebounced = undefined;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mapController]);

	useEffect(() => {
		if (userPosition && mapController) {
			mapController.drawMarker(userPosition, 'user', true);

			/**
			 * Focus on the user's position. Do it only once and if the initial target point wasn't set before
			 * */
			if (!initialGeoFieldValue?.position && !lastUserPositionRef.current) {
				mapController.focusOnPos(userPosition);
			}
		}

		lastUserPositionRef.current = userPosition;
	}, [initialGeoFieldValue, mapController, userPosition]);

	/** When user's location become known AND no location was inputed or picked by map, set user's location as a target point */
	useEffect(() => {
		if (userPosition && !initialGeoFieldValue?.position && !pos) {
			setPos(userPosition);
		}
	}, [initialGeoFieldValue, pos, userPosition]);

	/**
	 * If a user has earlier typed and selected a location using the input and the autosuggest, then reuse that location id and lookup for its IlocationDecoded
	 * It is needed to set the correct initial value of ILocationDecoded for this hook state
	 * */
	useEffect(() => {
		(async () => {
			if (initialGeoFieldValue?.id) {
				try {
					const loc = await lookupById(initialGeoFieldValue.id);
					setLocationDecoded(loc);
				} catch (e) {
					logger.error('Error while decoding location', e);
				}
			}
		})();
	}, [initialGeoFieldValue, logger]);

	/** If a user tapped on the map, then reverseGeocode the lat,lng pair (convert it to the ILocationDecoded) */
	useEffect(() => {
		(async () => {
			if (pos) {
				try {
					const found = await reverseGeocode(pos.toString(), 1);
					setLocationDecoded(found[0] || null);
				} catch (e) {
					Sentry.captureException(e);
				} finally {
					setReverseGeocoding(false);
				}
			}
		})();
	}, [pos]);

	return { locationDecoded, isReverseGeocoding };
};
